import { ApiSet, BaseResponse, IndexApiSet, useDeleteApi, useDownloadApi, useIndexApi, usePostApi, usePutApi } from 'utils/network/api_hooks'
import { Form, useEffectSkipFirst } from 'utils/hooks'
import { CraifHttpClient } from '../utils/network/axios'
import { PagingResponse } from 'entities'
import { PcrError, PcrErrorForm, PcrErrorSearchForm } from 'entities/pcr_error'

type PcrErrorsResponse = PagingResponse & {
    results: PcrError[]
}

export function useFetchPcrErrorsApi(
    searchForm?: Form<PcrErrorSearchForm>
): IndexApiSet<PcrErrorsResponse> & { execute: () => void; executeAndPageReset: () => void } {
    const apiPath = 'pcr_errors/'
    const api = useIndexApi<PcrErrorsResponse>(new CraifHttpClient(), {
        initialResponse: { count: 0, results: [] },
    })

    const execute = (): void => {
        api.execute(apiPath, { params: searchForm?.object })
    }

    const executeAndPageReset = (): void => {
        if (api.pageSet.page == 1) {
            execute()
        } else {
            api.pageSet.setPage(1)
        }
    }

    useEffectSkipFirst(() => {
        execute()
    }, [api.pageSet.page, api.pageSet.perPage])

    useEffectSkipFirst(() => {
        executeAndPageReset()
    }, [searchForm?.object])

    return { ...api, execute: execute, executeAndPageReset: executeAndPageReset }
}

export function useDownloadCsvPcrErrorsApi(searchForm: Form<PcrErrorSearchForm>): ApiSet<BaseResponse> & { execute: () => void } {
    const apiPath = 'pcr_errors/csv/'
    const api = useDownloadApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (): void => {
        api.execute(apiPath, 'pcrError', searchForm.object)
    }

    return { ...api, execute: execute }
}

export function usePostPcrErrorApi(): ApiSet<BaseResponse> & { execute: (form: Form<PcrErrorForm>) => void } {
    const apiSet = usePostApi<BaseResponse, PcrErrorForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<PcrErrorForm>) => {
        const apiPath = `pcr_errors/`
        apiSet.execute(apiPath, form)
    }

    return { ...apiSet, execute: execute }
}

export function usePutPcrErrorApi(): ApiSet<BaseResponse> & { execute: (form: Form<PcrErrorForm>) => void } {
    const apiSet = usePutApi<BaseResponse, PcrErrorForm>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (form: Form<PcrErrorForm>) => {
        const apiPath = `pcr_errors/${form.object.id}/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useBulkPutPcrErrorsApi(): ApiSet<BaseResponse> & { execute: (form: Form<PcrErrorForm[]>) => void } {
    const apiSet = usePutApi<BaseResponse, PcrErrorForm[]>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<PcrErrorForm[]>) => {
        const apiPath = `pcr_errors/bulk_update/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useDeletePcrErrorApi(): ApiSet<BaseResponse> & {
    execute: (id: number) => void
} {
    const api = useDeleteApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number): void => {
        const apiPath = `pcr_errors/${id}/`
        api.execute(apiPath)
    }

    return { ...api, execute: execute }
}
